import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useIsVisible from "../../../../../hooks/useIsVisible";
import { usePrevious } from "../../../../../hooks/usePrevious";
import CompareSelected from "../../../../../parts/CompareSelected";
import { MobileMonthSelector } from "../../../../../parts/MobileMonthSelector";
import SvgIcon from "../../../../../parts/SvgIcon";
import { ReportsBackHandler } from "../../components/ReportsBackHandler";
import CompareListPhotosModal from "./CompareListPhotosModal";
import ListViewItem from "./ListViewItem";
import MonthSelector from "./MonthSelector";

const ListView = ({ locals, comesFromBodyComp }) => {
  const { t } = useTranslation("athlete-portal");
  const { dailyStats, query } = locals;
  const { _name, _id: athleteId } = locals.athlete || locals.activeUser;
  const isCoach = !!locals.athlete;
  const today = query.date ? moment.utc(query.date, "YYYYMMDD") : moment();
  const previousToday = usePrevious(today);
  const organizationCoach = query.organizationCoach || undefined;

  const organizationCoachParam = organizationCoach
    ? `&organizationCoach=${organizationCoach}`
    : "";
  const timelineUrl = isCoach
    ? `/athlete-portal/metrics/progress-photos?date=${moment(today).format(
        "YYYYMMDD"
      )}&athlete=${athleteId}&view=Timeline`
    : `/dashboard/metrics/progress-photos?date=${moment(today).format(
        "YYYYMMDD"
      )}&view=Timeline`;
  const [date, setDate] = useState(today);
  const [listOfPhotos, setListOfPhotos] = useState([]);

  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [isSelecting, setIsSelecting] = useState(false);

  const [isModalOpened, setIsModalOpened] = useState(false);

  const ref = useRef();
  const isVisible = useIsVisible(ref);

  // Update date state when today changes
  useEffect(() => {
    if (previousToday?.format("YYYYMMDD") !== today?.format("YYYYMMDD")) {
      setDate(today);
    }
  }, [today, previousToday]);

  const getFilteredStats = useCallback(
    (startOfMonth, endOfMonth) => {
      const photos = dailyStats.filter(
        (stat) =>
          stat.bodyPhotos &&
          Object.keys(stat.bodyPhotos).length &&
          stat.utcDate >= startOfMonth &&
          stat.utcDate <= endOfMonth
      );
      return photos;
    },
    [dailyStats]
  );

  const handleReset = () => {
    setIsModalOpened(false);
    setSelectedPhotos([]);
    setIsSelecting(false);
  };
  const selectPhotos = (photo) => {
    if (selectedPhotos.length <= 2) {
      // Add or remove photo based on its selection state
      setSelectedPhotos((prevSelectedPhotos) => {
        if (prevSelectedPhotos.includes(photo)) {
          return prevSelectedPhotos.filter(
            (selectedPhoto) => selectedPhoto !== photo
          );
        }
        return selectedPhotos.length < 2
          ? [...prevSelectedPhotos, photo]
          : prevSelectedPhotos;
      });
    }
  };

  useEffect(() => {
    setListOfPhotos(
      getFilteredStats(
        moment(date).utc().startOf("month").format("YYYYMMDD"),
        moment(date).utc().endOf("month").format("YYYYMMDD")
      )
    );
  }, [date, getFilteredStats]);

  return (
    <article role="report-content" id="progress-photos">
      <header>
        <div className="top-header only-show-mobile">
          <ReportsBackHandler
            title={t("progressPhotos.title")}
            locals={locals}
          />
          <MobileMonthSelector date={date} setDate={setDate} />
        </div>
        <div className="flex gap-2 items-center">
          {comesFromBodyComp && (
            <SvgIcon
              classNames="cursor-pointer "
              onClickFn={() => {
                history.back();
              }}
              src={"/img/new-icons/back-button.svg"}
            />
          )}
          <h3 className="mobile-hidden">{t("progressPhotos.title")}</h3>
        </div>
        <div ref={ref} role="buttons-container">
          {isSelecting ? (
            <>
              <button
                type="button"
                data-button-type="secondary"
                onClick={handleReset}
              >
                {t("calendar.cancel")}
              </button>
              <button
                type="button"
                data-button-type="primary"
                disabled={selectedPhotos.length < 2}
                onClick={
                  selectedPhotos.length === 2
                    ? () => setIsModalOpened(true)
                    : null
                }
              >
                {t("progressPhotos.compare")}
              </button>
            </>
          ) : (
            <>
              <a
                href={`${timelineUrl}${organizationCoachParam}`}
                data-button-type="secondary"
                role="button"
              >
                {t("progressPhotos.timelineView")}
              </a>
              <button
                type="button"
                data-button-type="primary"
                onClick={() => setIsSelecting(true)}
              >
                {t("progressPhotos.comparePhotos")}
              </button>
            </>
          )}
        </div>
      </header>
      <div id="listview-container">
        <MonthSelector date={date} setDate={setDate} />
        {listOfPhotos.length > 0 && (
          <ul className="photos-list">
            {listOfPhotos.map((item) => (
              <ListViewItem
                key={item._id}
                stat={item}
                choosing={isSelecting}
                selectPhotos={selectPhotos}
                selectedPhotos={selectedPhotos}
              />
            ))}
          </ul>
        )}
        {listOfPhotos.length === 0 && <p>{t("progressPhotos.noData")}</p>}
      </div>
      {
        <CompareSelected
          label1={t("progressPhotos.image1")}
          label2={t("progressPhotos.image2")}
          selectedItem1={
            selectedPhotos[0]
              ? moment(selectedPhotos[0].date, "YYYYMMDD").format("MMM D,YYYY")
              : null
          }
          selectedItem2={
            selectedPhotos[1]
              ? moment(selectedPhotos[1].date, "YYYYMMDD").format("MMM D,YYYY")
              : null
          }
          compareFunction={() => setIsModalOpened(true)}
          closeFunction={() => setSelectedPhotos([])}
          isTopButtonVisible={isVisible}
          compareName={"Photos"}
          isModalOpened={isModalOpened}
        />
      }
      {isModalOpened && (
        <CompareListPhotosModal
          selectedPhotos={[...selectedPhotos].sort(
            (a, b) => a.utcDate - b.utcDate
          )}
          setSelectedPhotos={setSelectedPhotos}
          closeModalAndReset={handleReset}
          closeModal={() => setIsModalOpened(false)}
          sides={["front", "back", "side"]}
        />
      )}
    </article>
  );
};

export default ListView;
